import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, AlertCircle, Target, Sparkles } from "lucide-react";

export default function AssetPredictionCard({ prediction, predictionLoading, onGenerate }) {
  const [timeframe, setTimeframe] = useState("short");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <Card className="glass-effect">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-[#00E0FF]" />
              <CardTitle className="text-white" style={{ fontFamily: 'Unbounded' }}>
                AI PREDICTION
              </CardTitle>
            </div>
            <div className="flex items-center gap-4">
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger data-testid="timeframe-select" className="w-[160px] bg-black/20 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F19] border-white/10">
                  <SelectItem value="short">Short-term</SelectItem>
                  <SelectItem value="long">Long-term</SelectItem>
                </SelectContent>
              </Select>
              <Button
                data-testid="generate-prediction-btn"
                onClick={() => onGenerate(timeframe)}
                disabled={predictionLoading}
                className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold"
              >
                {predictionLoading ? (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Generate Prediction
                  </span>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {prediction ? (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="glass-effect p-4 rounded-lg">
                  <div className="text-sm text-gray-400 mb-1">Confidence</div>
                  <div className="text-2xl font-bold text-[#00E0FF] mono">{prediction.confidence}</div>
                </div>
                <div className="glass-effect p-4 rounded-lg">
                  <div className="text-sm text-gray-400 mb-1">Timeframe</div>
                  <div className="text-2xl font-bold text-white capitalize mono">{prediction.timeframe}</div>
                </div>
                <div className="glass-effect p-4 rounded-lg">
                  <div className="text-sm text-gray-400 mb-1">Risk Level</div>
                  <div className={`text-2xl font-bold mono ${
                    prediction.risk_level === 'Low' ? 'text-[#00FFA3]' :
                    prediction.risk_level === 'Medium' ? 'text-[#F59E0B]' : 'text-[#FF3B30]'
                  }`}>{prediction.risk_level}</div>
                </div>
              </div>

              <div className="glass-effect p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-[#00E0FF]" />
                  <h3 className="font-bold text-white">AI Analysis</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">{prediction.prediction}</p>
              </div>

              <div className="glass-effect p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-[#00FFA3]" />
                  <h3 className="font-bold text-white">Recommendations</h3>
                </div>
                <ul className="space-y-2">
                  {prediction.recommendations.map((rec) => (
                    <li key={rec} className="flex items-start gap-2 text-gray-300">
                      <span className="text-[#00E0FF] mt-1">•</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Brain className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Select a timeframe and click "Generate Prediction" to get AI insights</p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
